import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Sparkles, Crown, TrendingDown, Clock, Shield } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3" data-testid="logo">
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50] tracking-tight">
                LuxLyx
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                data-testid="login-btn"
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
              >
                Login
              </Button>
              <Button
                data-testid="register-btn"
                onClick={() => navigate('/register')}
                className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] shadow-sm"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-24 px-6">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1A3A52]/5 border border-[#1A3A52]/10 rounded-full px-5 py-2 mb-10">
            <Sparkles className="h-4 w-4 text-[#1A3A52]" strokeWidth={2} />
            <span className="text-[#1A3A52] text-sm font-medium">AI-Powered Liquidation Marketplace</span>
          </div>
          
          <h1 className="text-6xl lg:text-7xl font-serif font-semibold mb-6 text-[#2C3E50] leading-tight tracking-tight">
            Curated Flash Sales
            <br />
            <span className="text-[#1A3A52] italic">
              Premium Overstock
            </span>
          </h1>
          
          <p className="text-xl text-[#5A6C7D] mb-12 max-w-2xl mx-auto leading-relaxed">
            Discover exceptional value on excess inventory from distinguished D2C brands.
            Limited quantities. Refined selection. AI-optimized pricing.
          </p>
          
          <div className="flex items-center justify-center space-x-4">
            <Button
              data-testid="shop-now-btn"
              onClick={() => navigate('/products')}
              size="lg"
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium text-base px-8 py-6 shadow-md"
            >
              <ShoppingBag className="mr-2 h-5 w-5" strokeWidth={1.5} />
              Shop Collection
            </Button>
            <Button
              data-testid="membership-btn"
              onClick={() => navigate('/membership')}
              size="lg"
              variant="outline"
              className="border-2 border-[#1A3A52] text-[#1A3A52] hover:bg-[#1A3A52]/5 font-medium text-base px-8 py-6"
            >
              <Crown className="mr-2 h-5 w-5" strokeWidth={1.5} />
              Join Premium
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-12 mt-20 max-w-3xl mx-auto">
            <div className="border-r border-[#E5E1DC] last:border-0">
              <div className="text-5xl font-serif font-semibold text-[#1A3A52]">500+</div>
              <div className="text-[#8B9AAC] mt-2 text-sm uppercase tracking-wider">Premium Brands</div>
            </div>
            <div className="border-r border-[#E5E1DC] last:border-0">
              <div className="text-5xl font-serif font-semibold text-[#1A3A52]">70%</div>
              <div className="text-[#8B9AAC] mt-2 text-sm uppercase tracking-wider">Avg Savings</div>
            </div>
            <div>
              <div className="text-5xl font-serif font-semibold text-[#1A3A52]">24hrs</div>
              <div className="text-[#8B9AAC] mt-2 text-sm uppercase tracking-wider">Flash Sales</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <h2 className="text-4xl font-serif font-semibold text-center mb-20 text-[#2C3E50]">Why Choose LuxLyx</h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3A52]/5 mb-6">
                <TrendingDown className="h-8 w-8 text-[#1A3A52]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3 text-[#2C3E50]">AI-Optimized Pricing</h3>
              <p className="text-[#5A6C7D] leading-relaxed">
                Intelligent analysis of market dynamics to ensure optimal value on every purchase.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3A52]/5 mb-6">
                <Clock className="h-8 w-8 text-[#1A3A52]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3 text-[#2C3E50]">Exclusive Access</h3>
              <p className="text-[#5A6C7D] leading-relaxed">
                Time-limited collections with authentic scarcity. Once available, never repeated.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3A52]/5 mb-6">
                <Shield className="h-8 w-8 text-[#1A3A52]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3 text-[#2C3E50]">Verified Quality</h3>
              <p className="text-[#5A6C7D] leading-relaxed">
                Authentic products from curated D2C brands. Excellence guaranteed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <div className="bg-white border border-[#E5E1DC] rounded-2xl p-16 text-center shadow-lg">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1A3A52]/5 mb-8">
              <Crown className="h-10 w-10 text-[#1A3A52]" strokeWidth={1.5} />
            </div>
            <h2 className="text-4xl font-serif font-semibold mb-4 text-[#2C3E50]">Elevate Your Experience</h2>
            <p className="text-lg text-[#5A6C7D] mb-10 max-w-2xl mx-auto leading-relaxed">
              Early access to collections, additional 10% savings, complimentary shipping, and personalized AI recommendations.
            </p>
            <Button
              data-testid="join-premium-btn"
              onClick={() => navigate('/membership')}
              size="lg"
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium text-base px-10 py-6 shadow-md"
            >
              Join Premium — $9.99/month
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E1DC] py-10 px-6 bg-white">
        <div className="container mx-auto text-center text-[#8B9AAC] text-sm">
          <p>&copy; 2025 LuxLyx. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
